import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  BellRing, MessageSquare, Phone, Mail, Plus, Trash2, Send,
  ChevronDown, ChevronUp, ArrowDown, AlertOctagon,
} from "lucide-react";

const SEVERITIES = ["critical", "high", "medium", "warning", "info"];

export default function AlertDispatch() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expandedTemplates, setExpandedTemplates] = useState(true);

  const load = () => api.get("/alert-dispatch/settings").then((r) => setSettings(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try { await api.put("/alert-dispatch/settings", settings); toast.success("Dispatch settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const addContact = () => setSettings({
    ...settings,
    contacts: [...settings.contacts, {
      contact_id: `dc_${Math.random().toString(36).slice(2, 12)}`,
      name: "", role: "Site Engineer", phone: "", whatsapp: "", email: "",
      severities: ["critical", "high"], is_active: true,
    }],
  });

  const updateContact = (idx, patch) => {
    const copy = [...settings.contacts];
    copy[idx] = { ...copy[idx], ...patch };
    setSettings({ ...settings, contacts: copy });
  };

  const deleteContact = (idx) => setSettings({ ...settings, contacts: settings.contacts.filter((_, i) => i !== idx) });

  const toggleSeverity = (idx, sev) => {
    const c = settings.contacts[idx];
    const next = c.severities.includes(sev) ? c.severities.filter((s) => s !== sev) : [...c.severities, sev];
    updateContact(idx, { severities: next });
  };

  const testAlert = async (contact_id, channel) => {
    try { await api.post("/alert-dispatch/test", { contact_id, channel });
      toast.success(`Test alert queued via ${channel.toUpperCase()}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  if (!settings) return <div className="text-gray-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="alert-dispatch-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-2">
            <BellRing className="w-8 h-8 text-orange-400" strokeWidth={2.5} /> Alert Dispatch Settings
          </h1>
          <p className="text-sm text-gray-500 mt-1">WhatsApp · SMS · Email routing for ACMS alerts with auto-escalation</p>
        </div>
        <Button onClick={save} disabled={busy} className="f360-gradient border-0" data-testid="save-dispatch-btn">Save Settings</Button>
      </div>

      {/* CHANNEL TOGGLES */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { key: "enable_whatsapp", label: "WhatsApp", icon: MessageSquare, tone: "from-emerald-500 to-green-600" },
          { key: "enable_sms",      label: "SMS",      icon: Phone,         tone: "from-sky-500 to-indigo-600" },
          { key: "enable_email",    label: "Email",    icon: Mail,          tone: "from-orange-500 to-pink-600" },
        ].map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} className="glass rounded-lg p-5 flex items-center gap-4" data-testid={`channel-${c.key}`}>
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${c.tone} flex items-center justify-center shadow-lg`}>
                <Icon className="w-6 h-6 text-gray-900" />
              </div>
              <div className="flex-1">
                <div className="font-bold text-lg">{c.label}</div>
                <div className="text-xs text-gray-500">{settings[c.key] ? "Active" : "Disabled"}</div>
              </div>
              <Switch checked={settings[c.key]} onCheckedChange={(v) => setSettings({ ...settings, [c.key]: v })} data-testid={`toggle-${c.key}`} />
            </div>
          );
        })}
      </div>

      {/* CONTACTS TABLE */}
      <div className="glass rounded-lg overflow-hidden" data-testid="dispatch-contacts">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-bold text-lg">Alert Contacts</h2>
          <Button size="sm" onClick={addContact} className="f360-gradient border-0" data-testid="add-contact-btn">
            <Plus className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Add Contact
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: "900px" }}>
            <thead className="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
              <tr>
                <th className="text-left px-3 py-3" style={{ minWidth: "150px" }}>Name</th>
                <th className="text-left px-2 py-3" style={{ minWidth: "130px" }}>Role</th>
                <th className="text-left px-2 py-3" style={{ minWidth: "180px" }}>Phone / WhatsApp</th>
                <th className="text-left px-2 py-3" style={{ minWidth: "200px" }}>Email</th>
                <th className="text-left px-2 py-3" style={{ minWidth: "150px" }}>Severities</th>
                <th className="text-center px-2 py-3" style={{ minWidth: "70px" }}>Test</th>
                <th className="text-center px-3 py-3" style={{ minWidth: "50px" }}>—</th>
              </tr>
            </thead>
            <tbody>
              {settings.contacts.map((c, idx) => (
                <tr key={c.contact_id} className="border-t border-gray-200 align-top" data-testid={`contact-row-${idx}`}>
                  <td className="px-3 py-2.5"><Input className="bg-gray-50 border-gray-200 h-8 w-full" value={c.name} onChange={(e) => updateContact(idx, { name: e.target.value })} placeholder="Full name" /></td>
                  <td className="px-2 py-2.5"><Input className="bg-gray-50 border-gray-200 h-8 w-full" value={c.role} onChange={(e) => updateContact(idx, { role: e.target.value })} placeholder="Site Engineer" /></td>
                  <td className="px-2 py-2.5">
                    <Input className="bg-gray-50 border-gray-200 h-8 mb-1 w-full" value={c.phone || ""} onChange={(e) => updateContact(idx, { phone: e.target.value })} placeholder="+91 98XXX XXXXX" />
                    <Input className="bg-gray-50 border-gray-200 h-8 w-full" value={c.whatsapp || ""} onChange={(e) => updateContact(idx, { whatsapp: e.target.value })} placeholder="WhatsApp" />
                  </td>
                  <td className="px-2 py-2.5"><Input className="bg-gray-50 border-gray-200 h-8 w-full" value={c.email || ""} onChange={(e) => updateContact(idx, { email: e.target.value })} placeholder="alerts@example.com" /></td>
                  <td className="px-2 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {SEVERITIES.map((s) => (
                        <button key={s} onClick={() => toggleSeverity(idx, s)} className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${c.severities.includes(s) ? "bg-orange-500/30 text-orange-200" : "bg-white/5 text-slate-500"}`}>{s}</button>
                      ))}
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    <div className="flex flex-col gap-1 items-center">
                      <button onClick={() => testAlert(c.contact_id, "whatsapp")} className="text-emerald-300 hover:text-gray-900 text-[10px]" data-testid={`test-wa-${idx}`}>WA</button>
                      <button onClick={() => testAlert(c.contact_id, "sms")} className="text-sky-300 hover:text-gray-900 text-[10px]">SMS</button>
                      <button onClick={() => testAlert(c.contact_id, "email")} className="text-orange-300 hover:text-gray-900 text-[10px]">Email</button>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-center"><button onClick={() => deleteContact(idx)} className="text-red-400 hover:text-red-200" data-testid={`del-${idx}`}><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button></td>
                </tr>
              ))}
              {settings.contacts.length === 0 && <tr><td colSpan={7} className="text-center text-gray-500 py-6">No contacts. Click + Add Contact to get started.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* ESCALATION MATRIX */}
      <div className="glass rounded-lg p-5" data-testid="escalation-matrix">
        <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><ArrowDown className="w-5 h-5 text-orange-400" strokeWidth={2.5} /> Escalation Matrix</h2>
        <div className="space-y-3">
          {(settings.escalation || []).map((lv, i) => (
            <div key={lv.level} className="flex items-center gap-4 p-3 rounded-lg bg-gray-50">
              <div className="w-10 h-10 rounded-full f360-gradient flex items-center justify-center font-bold text-white shrink-0">{lv.level}</div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Delay (minutes)</div>
                  <Input type="number" className="bg-gray-50 border-gray-200 h-8" value={lv.delay_minutes}
                    onChange={(e) => {
                      const copy = [...settings.escalation];
                      copy[i] = { ...lv, delay_minutes: Number(e.target.value) };
                      setSettings({ ...settings, escalation: copy });
                    }} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Notify Role</div>
                  <Input className="bg-gray-50 border-gray-200 h-8" value={lv.role}
                    onChange={(e) => {
                      const copy = [...settings.escalation];
                      copy[i] = { ...lv, role: e.target.value };
                      setSettings({ ...settings, escalation: copy });
                    }} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-gray-500 mt-3">Alerts re-fire to the next level if not acknowledged within the delay window.</p>
      </div>

      {/* MESSAGE TEMPLATES */}
      <div className="glass rounded-lg p-5" data-testid="message-templates">
        <button onClick={() => setExpandedTemplates(!expandedTemplates)} className="flex items-center justify-between w-full">
          <h2 className="font-bold text-lg flex items-center gap-2"><AlertOctagon className="w-5 h-5 text-orange-400" strokeWidth={2.5} /> Message Templates</h2>
          {expandedTemplates ? <ChevronUp className="w-5 h-5" strokeWidth={2.5} /> : <ChevronDown className="w-5 h-5" strokeWidth={2.5} />}
        </button>
        {expandedTemplates && (
          <div className="mt-4 space-y-4">
            <p className="text-xs text-gray-500">Use variables: <code className="bg-gray-100 px-1 rounded">{"{{site_name}}"}</code>, <code className="bg-gray-100 px-1 rounded">{"{{alarm_type}}"}</code>, <code className="bg-gray-100 px-1 rounded">{"{{location}}"}</code>, <code className="bg-gray-100 px-1 rounded">{"{{time}}"}</code>, <code className="bg-gray-100 px-1 rounded">{"{{pressure_value}}"}</code></p>
            {Object.entries(settings.templates || {}).map(([sev, txt]) => (
              <div key={sev}>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">{sev}</div>
                <Textarea rows={3} className="bg-gray-50 border-gray-200" value={txt}
                  onChange={(e) => setSettings({ ...settings, templates: { ...settings.templates, [sev]: e.target.value } })}
                  data-testid={`template-${sev}`} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
